import React, { useState, useEffect } from 'react';
import { Modal, Form, Select, Button, Space, message, Typography, Radio, Table } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import axios from 'axios';
import { API_URL } from '../config';

const { Text, Title } = Typography;
const { Option } = Select;

interface BulkCreateRoomsModalProps {
  visible: boolean;
  hotelId: number;
  roomTypeId: number;
  roomTypeName: string;
  emHotelId?: number;
  onClose: () => void;
  onSuccess: () => void;
}

interface LanguageMapping {
  mewsLanguage: string;
  emLanguageId: number;
  emLanguageCode: string;
}

const MEWS_LANGUAGES = [
  { code: 'en-US', label: 'English (US)' },
  { code: 'de-DE', label: 'German' },
  { code: 'fr-FR', label: 'French' },
  { code: 'it-IT', label: 'Italian' },
  { code: 'es-ES', label: 'Spanish' },
  { code: 'nl-NL', label: 'Dutch' },
];

const LANGUAGE_MATCH_MAP: Record<string, string[]> = {
  'en-US': ['english', 'en', 'en-us', 'en-gb'],
  'en-GB': ['english', 'en', 'en-us', 'en-gb'],
  'nl-NL': ['dutch', 'nl', 'nl-nl', 'nederlands'],
  'de-DE': ['german', 'de', 'de-de', 'deutsch'],
  'fr-FR': ['french', 'fr', 'fr-fr', 'français'],
  'es-ES': ['spanish', 'es', 'es-es', 'español'],
  'it-IT': ['italian', 'it', 'it-it', 'italiano'],
};

function autoMatchLanguages(
  mewsLanguages: { code: string; label: string }[],
  emLanguages: { id: number; code: string }[],
): LanguageMapping[] {
  const matched: LanguageMapping[] = [];
  for (const mewsLang of mewsLanguages) {
    const possibleMatches = LANGUAGE_MATCH_MAP[mewsLang.code] || [mewsLang.code.split('-')[0], mewsLang.code];
    const emMatch = emLanguages.find(emLang =>
      possibleMatches.some(m => emLang.code.toLowerCase() === m)
    );
    if (emMatch) {
      matched.push({
        mewsLanguage: mewsLang.code,
        emLanguageId: emMatch.id,
        emLanguageCode: emMatch.code,
      });
    }
  }
  return matched;
}

const BulkCreateRoomsModal: React.FC<BulkCreateRoomsModalProps> = ({
  visible,
  hotelId,
  roomTypeId,
  roomTypeName,
  emHotelId,
  onClose,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [emLanguages, setEmLanguages] = useState<{ id: number; code: string; label: string }[]>([]);
  const [languageMappings, setLanguageMappings] = useState<LanguageMapping[]>([
    { mewsLanguage: '', emLanguageId: 0, emLanguageCode: '' },
  ]);
  const [roomCount, setRoomCount] = useState<number>(0);
  const [actualRoomTypeName, setActualRoomTypeName] = useState<string>('');
  const [roomTypeData, setRoomTypeData] = useState<any>(null);
  const [descriptionSource, setDescriptionSource] = useState<'em' | 'mews'>('mews');
  const [rooms, setRooms] = useState<any[]>([]);

  useEffect(() => {
    if (visible && roomTypeId !== undefined) {
      loadEmLanguages();
      loadRoomTypeData();
      loadRoomCount();
    } else if (!visible) {
      // Reset form when modal closes
      form.resetFields();
      setLanguageMappings([{ mewsLanguage: '', emLanguageId: 0, emLanguageCode: '' }]);
      setRoomCount(0);
      setActualRoomTypeName('');
      setRoomTypeData(null);
      setDescriptionSource('mews');
      setRooms([]);
    }
  }, [visible, roomTypeId]);

  const loadEmLanguages = async () => {
    try {
      const response = await axios.get(`${API_URL}/em-push/languages`);
      const langs = response.data;
      setEmLanguages(langs);

      // Auto-match language mappings
      const matched = autoMatchLanguages(MEWS_LANGUAGES, langs);
      if (matched.length > 0) {
        setLanguageMappings(matched);
      }
    } catch (error) {
      message.error('Failed to load EM languages');
    }
  };

  const loadRoomTypeData = async () => {
    // Skip loading room type data if pushing all rooms
    if (roomTypeId === 0) {
      setActualRoomTypeName(roomTypeName);
      return;
    }

    try {
      const response = await axios.get(`${API_URL}/room-types/${roomTypeId}`);
      const data = response.data;
      setRoomTypeData(data);

      // Set the actual room type name
      const name = data.names?.['en-US'] || data.names?.['nl-NL'] || Object.values(data.names || {})[0] || roomTypeName;
      setActualRoomTypeName(name);
    } catch (error) {
      message.error('Failed to load room type data');
      setActualRoomTypeName(roomTypeName);
    }
  };

  const loadRoomCount = async () => {
    try {
      const response = await axios.get(`${API_URL}/hotels/${hotelId}/rooms`);
      const allRooms = response.data;

      // If roomTypeId is 0, get all rooms, otherwise filter by room type
      const roomsOfType = roomTypeId === 0
        ? allRooms
        : allRooms.filter((room: any) => room.roomTypeId === roomTypeId);

      setRoomCount(roomsOfType.length);

      // Also fetch full room details for preview (limit to first 10 for performance)
      const roomDetailsPromises = roomsOfType.slice(0, 10).map((room: any) =>
        axios.get(`${API_URL}/rooms/${room.id}`)
      );
      const roomDetails = await Promise.all(roomDetailsPromises);
      setRooms(roomDetails.map(r => r.data));
    } catch (error) {
      message.error('Failed to load room count');
    }
  };

  const handleAddMapping = () => {
    setLanguageMappings([...languageMappings, { mewsLanguage: '', emLanguageId: 0, emLanguageCode: '' }]);
  };

  const handleRemoveMapping = (index: number) => {
    const newMappings = languageMappings.filter((_, i) => i !== index);
    setLanguageMappings(newMappings);
  };

  const handleMewsLanguageChange = (index: number, value: string) => {
    const newMappings = [...languageMappings];
    newMappings[index].mewsLanguage = value;
    setLanguageMappings(newMappings);
  };

  const handleEmLanguageChange = (index: number, value: number) => {
    const newMappings = [...languageMappings];
    const selectedLang = emLanguages.find(lang => lang.id === value);
    newMappings[index].emLanguageId = value;
    newMappings[index].emLanguageCode = selectedLang?.code || '';
    setLanguageMappings(newMappings);
  };

  const handleSubmit = async () => {
    if (!emHotelId) {
      message.error('EM Hotel ID is required');
      return;
    }

    // Validate that all mappings are filled
    const hasEmptyMappings = languageMappings.some(
      (m) => !m.mewsLanguage || !m.emLanguageId
    );

    if (hasEmptyMappings) {
      message.error('Please fill all language mappings');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/rooms/bulk-create`, {
        hotelId,
        roomTypeId,
        emHotelId,
        descriptionSource,
        languageMappings,
      });

      const { created, failed, results } = response.data;

      if (failed > 0) {
        message.warning(`Pushed ${created} rooms, ${failed} failed. Check console for details.`);
        console.log('Bulk push results:', results);
      } else {
        message.success(`Successfully pushed ${created} rooms to EM!`);
      }

      onSuccess();
      onClose();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to push rooms');
    } finally {
      setLoading(false);
    }
  };

  const getUsedMewsLanguages = () => {
    return languageMappings.map((m) => m.mewsLanguage).filter(Boolean);
  };

  const getUsedEmLanguages = () => {
    return languageMappings.map((m) => m.emLanguageId).filter(id => id > 0);
  };

  return (
    <Modal
      title={`Bulk Populate EM Content for Rooms`}
      open={visible}
      onCancel={onClose}
      width={700}
      style={{ top: 20 }}
      footer={[
        <Button key="cancel" onClick={onClose}>
          Cancel
        </Button>,
        <Button key="submit" type="primary" onClick={handleSubmit} loading={loading}>
          Populate Content
        </Button>,
      ]}
      styles={{
        body: {
          maxHeight: 'calc(100vh - 200px)',
          overflowY: 'auto',
          padding: '12px 24px 16px',
        },
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ padding: '8px 12px', background: '#f0f5ff', borderRadius: 6, fontSize: 13 }}>
          <Text strong>Room Type:</Text> {actualRoomTypeName || roomTypeName} &nbsp;|&nbsp; <Text strong>Rooms:</Text> {roomCount}
        </div>

        <div>
          <Text strong style={{ fontSize: 13 }}>
            Language Mappings (Source to EM)
          </Text>
          <Text type="secondary" style={{ display: 'block', marginBottom: 8, fontSize: 12 }}>
            Map source languages to EM languages. Room names will use the room number from the source PMS.
          </Text>

          <Space direction="vertical" size="small" style={{ width: '100%' }}>
            {languageMappings.map((mapping, index) => (
              <Space key={index} align="start" style={{ width: '100%' }}>
                <Select
                  style={{ width: 200 }}
                  placeholder="Select Source Language"
                  value={mapping.mewsLanguage || undefined}
                  onChange={(value) => handleMewsLanguageChange(index, value)}
                >
                  {MEWS_LANGUAGES.map((lang) => (
                    <Option
                      key={lang.code}
                      value={lang.code}
                      disabled={getUsedMewsLanguages().includes(lang.code) && mapping.mewsLanguage !== lang.code}
                    >
                      {lang.label} ({lang.code})
                    </Option>
                  ))}
                </Select>

                <Text style={{ padding: '4px 8px' }}>→</Text>

                <Select
                  style={{ width: 200 }}
                  placeholder="Select EM Language"
                  value={mapping.emLanguageId || undefined}
                  onChange={(value) => handleEmLanguageChange(index, value)}
                >
                  {emLanguages.map((lang) => (
                    <Option
                      key={lang.id}
                      value={lang.id}
                      disabled={getUsedEmLanguages().includes(lang.id) && mapping.emLanguageId !== lang.id}
                    >
                      {lang.label}
                    </Option>
                  ))}
                </Select>

                {languageMappings.length > 1 && (
                  <Button
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => handleRemoveMapping(index)}
                  />
                )}
              </Space>
            ))}

            <Button
              type="dashed"
              icon={<PlusOutlined />}
              onClick={handleAddMapping}
              size="small"
              style={{ width: '100%', marginTop: 4 }}
            >
              Add Language Mapping
            </Button>
          </Space>
        </div>

        <div>
          <Text strong style={{ fontSize: 13 }}>
            Description Source
          </Text>
          <Text type="secondary" style={{ display: 'block', marginBottom: 8, fontSize: 12 }}>
            Choose which descriptions to use for EM content
          </Text>
          <Radio.Group
            value={descriptionSource}
            onChange={(e) => setDescriptionSource(e.target.value)}
          >
            <Space direction="vertical" size={4}>
              <Radio value="mews">
                <strong>Use Source Descriptions</strong>
                <div style={{ fontSize: 11, color: '#888', marginLeft: 24 }}>
                  Copy descriptions from the source PMS room type (if available)
                </div>
              </Radio>
              <Radio value="em">
                <strong>Use EM Descriptions</strong>
                <div style={{ fontSize: 11, color: '#888', marginLeft: 24 }}>
                  Copy descriptions from EM room type (if available)
                </div>
              </Radio>
            </Space>
          </Radio.Group>
        </div>

        <div>
          <Text strong style={{ fontSize: 13 }}>
            Room Preview ({roomCount} rooms, showing first 10)
          </Text>
          <Text type="secondary" style={{ display: 'block', marginBottom: 6, fontSize: 12 }}>
            These rooms will have their EM content populated with descriptions in all mapped languages
          </Text>
          <Table
            dataSource={rooms.slice(0, 10)}
            columns={[
              {
                title: 'Room',
                dataIndex: 'name',
                key: 'name',
                width: 100,
                fixed: 'left',
              },
              {
                title: 'Floor',
                dataIndex: 'floorNumber',
                key: 'floorNumber',
                width: 80,
                render: (val) => val || '-',
              },
              ...languageMappings
                .filter((m) => m.emLanguageId && m.mewsLanguage)
                .map((mapping, index) => ({
                  title: `Description (${mapping.emLanguageCode})`,
                  key: `desc_${index}`,
                  width: 250,
                  render: (record: any) => {
                    // Mirror the bulk-push fallback chain so the preview shows exactly
                    // what will get pushed. Keys are sqlite language codes (en-US, etc.).
                    const lang = mapping.mewsLanguage;
                    const trimmed = (v: unknown) =>
                      typeof v === 'string' && v.trim() !== '' ? v : undefined;
                    const primary = descriptionSource === 'em'
                      ? trimmed(roomTypeData?.emDescriptions?.[lang])
                      : trimmed(roomTypeData?.descriptions?.[lang]);
                    const secondary = descriptionSource === 'em'
                      ? trimmed(roomTypeData?.descriptions?.[lang])
                      : trimmed(roomTypeData?.emDescriptions?.[lang]);
                    const fromRoomEm = trimmed(record?.emDescriptions?.[lang]);
                    const fromRoomSrc = trimmed(record?.descriptions?.[lang]);

                    const desc = primary ?? fromRoomEm ?? fromRoomSrc ?? secondary;
                    if (!desc) return <Text type="secondary">-</Text>;

                    return (
                      <div
                        style={{
                          maxWidth: 240,
                          maxHeight: 60,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          fontSize: 12,
                        }}
                        dangerouslySetInnerHTML={{ __html: desc }}
                      />
                    );
                  },
                })),
            ]}
            pagination={false}
            scroll={{ x: 'max-content', y: 250 }}
            size="small"
            bordered
          />
        </div>
      </div>
    </Modal>
  );
};

export default BulkCreateRoomsModal;
